import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode, Ref } from 'react';
import type {
  ErpArticle,
  ErpArticleHolder,
  ErpConnectionView,
  ErpMappingRow,
  ErpMatchOutcome,
  ErpMatchReason,
  ErpNameCheck,
  ErpPendingMatch,
  ErpSaveState,
} from '../../../api';
import { erpApi } from '../../../api';
import { useKeepPrevious, useResource } from '../../../hooks';
import { useIdempotencyKey } from '../../../idempotency';
import { prefetch } from '../../../queryCache';
import { Callout, Checkbox, HelpTip, Icon, Input } from '../../primitives';
import { ConfirmDialog, ErrorNotice, LoadingRows, StatusBadge } from '../../ui';
import type { LinkedWebsite } from '../labels';
import { formatNumber } from '../labels';
import type { StoreCopy } from '../storeCopy';
import { useAction } from '../useAction';
import { CandidateCard } from './CandidateCard';
import { cannotSaveText, matchEvidenceLegend, refusalText } from './matchingCopy';
import type { SavedMatch } from './useRecentMatches';

const SEARCH_STEP = 6;

type Candidate = { article: ErpArticle; reasons?: ErpMatchReason[]; nameCheck?: ErpNameCheck; ambiguous?: boolean; suggested: boolean };

/**
 * The comparison itself (CO-170): the store product on the left with its
 * facts and the two things a person does with it, Match and next or Skip, and
 * on the right the ERP's candidates, with the ERP search beside the product so
 * neither has to be opened and closed. It does not know the queue; the panel
 * tells it where the product stands in it and what to do after a match.
 *
 * Saving asks the store (the store's attribute stays the source of truth), so
 * a match shows as "saving" until the store answers; where the store's
 * extension cannot save, the screen says so and offers only to copy a code,
 * never a "Matched" that is not so.
 */
export function MatchWorkspace({
  connection,
  target,
  row,
  attribute,
  pricesIncludeTax,
  words,
  canManage,
  save,
  place,
  nextSku,
  skipped,
  shortcutsOn,
  onSaved,
  onAdvance,
  onSkip,
  onStep,
  onUndo,
  canUndo,
  onChanged,
  onShowProduct,
  onBack,
  headingRef,
  status,
}: {
  connection: ErpConnectionView;
  target: LinkedWebsite;
  row: ErpMappingRow;
  attribute: string;
  pricesIncludeTax: boolean | null;
  words: StoreCopy;
  canManage: boolean;
  save: ErpSaveState;
  /** Where the product stands in the queue: its number, how many there are, and whether there is a product before and after. */
  place: { number: number; total: number; hasPrev: boolean; hasNext: boolean };
  /** The product Match and next goes to, so its suggestions are ready. */
  nextSku?: string;
  skipped: boolean;
  /** Letter and number shortcuts are live (no dialog is open). */
  shortcutsOn: boolean;
  /** A match was asked of the store. */
  onSaved: (saved: SavedMatch) => void;
  /** After a match: go to the next product. */
  onAdvance: () => void;
  onSkip: () => void;
  onStep: (direction: -1 | 1) => void;
  onUndo: () => void;
  canUndo: boolean;
  /** The store was asked something: the list is read again. */
  onChanged: () => void;
  onShowProduct: (sku: string) => void;
  /** Narrow screens: back to the queue. */
  onBack: () => void;
  headingRef: Ref<HTMLHeadingElement>;
  /** What was matched in this sitting (the panel keeps it, because it outlives a product). It sits under the actions, so it never moves them. */
  status?: ReactNode;
}) {
  const bySku = attribute === 'sku';
  const copyable = attribute !== '' && !bySku;
  const canSave = canManage && save.available;
  const needsMatch = row.state === 'no_code' || row.state === 'unmatched';
  const matched = row.state === 'matched' && Boolean(row.article);
  const action = useAction();
  const idempotency = useIdempotencyKey();
  const currency = connection.settings.price_currency ?? '';
  const syncedAt = connection.last_stock_sync_success_at;
  const searchId = useId();
  const groupName = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const [removing, setRemoving] = useState(false);
  // Taking an article from the product that carries it, or keeping a code on this
  // product in a conflict, waits for the person's agreement (CO-163).
  const [moving, setMoving] = useState<{ code: string; articleName: string; holder: { sku: string; name: string }; keep: boolean } | null>(null);
  const otherWebsites = (row.other_websites ?? []).map((code) => target.store.websites.find((website) => website.code === code)?.name || code);
  const productLabel = row.name || row.sku;

  // ---- candidates: what the engine suggests, then what the search finds ----
  const suggestions = useResource(
    () => erpApi.suggestions(connection.id, { installation_id: target.store.id, website_code: target.website.code, sku: row.sku }),
    [connection.id, target.store.id, target.website.code, row.sku, syncedAt],
    `${connection.id}:${target.key}:${row.sku}`,
    { key: `erp:${connection.id}:suggestions:${target.key}:${row.sku}`, enabled: needsMatch },
  );
  // The next product's suggestions are fetched while this one is worked on.
  useEffect(() => {
    if (!nextSku || !needsMatch) return;
    prefetch(`erp:${connection.id}:suggestions:${target.key}:${nextSku}`, () =>
      erpApi.suggestions(connection.id, { installation_id: target.store.id, website_code: target.website.code, sku: nextSku }),
    );
  }, [nextSku, needsMatch, connection.id, target.key, target.store.id, target.website.code]);

  // The search box starts from the product's own rarest words, and is the
  // person's from the first key they press.
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);
  const [query, setQuery] = useState('');
  const [freeOnly, setFreeOnly] = useState(true);
  const [shown, setShown] = useState(SEARCH_STEP);
  const hint = suggestions.data?.search_hint ?? '';
  useEffect(() => {
    if (!touched && hint) setText(hint);
  }, [hint, touched]);
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQuery(text.trim());
      setShown(SEARCH_STEP);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [text]);
  const asking = query !== '';
  const found = useResource(
    () =>
      erpApi.articles(connection.id, {
        installation_id: target.store.id,
        website_code: target.website.code,
        q: query,
        for_sku: row.sku,
        availability: freeOnly ? 'free' : undefined,
        limit: shown,
      }),
    [connection.id, target.store.id, target.website.code, row.sku, query, freeOnly, shown, syncedAt],
    `${connection.id}:${target.key}`,
    { key: `erp:${connection.id}:articles:${target.store.id}:${target.website.code}:${row.sku}:${query}:${freeOnly}:${shown}`, enabled: asking },
  );

  // Typing changes the query and with it the resource's key, which starts empty:
  // keep the last results on screen until the new ones arrive, so the list never
  // collapses to a skeleton or to "No article matches" for a moment (CO-175).
  const foundView = useKeepPrevious(found.data);
  const { suggestedEntries, resultEntries } = useMemo(() => {
    const suggested: Candidate[] = [];
    const results: Candidate[] = [];
    const seen = new Set<string>();
    if (needsMatch) {
      for (const item of suggestions.data?.items ?? []) {
        seen.add(item.article.code);
        suggested.push({ article: item.article, reasons: item.reasons, nameCheck: item.name_check, ambiguous: item.ambiguous, suggested: true });
      }
    }
    if (asking) {
      for (const article of foundView.data?.items ?? []) {
        if (!seen.has(article.code) && article.code !== row.erp_code) results.push({ article, suggested: false });
      }
    }
    return { suggestedEntries: suggested, resultEntries: results };
  }, [needsMatch, suggestions.data, asking, foundView.data, row.erp_code]);
  // Once the person has typed, what they asked for comes first, right under the
  // search box; the engine's suggestions follow. Before that, the suggestions
  // lead (CO-175). The numbers 1-9 follow what is on screen, top to bottom.
  const resultsFirst = needsMatch && touched && asking;
  const candidates = useMemo(
    () => (resultsFirst ? [...resultEntries, ...suggestedEntries] : [...suggestedEntries, ...resultEntries]),
    [resultsFirst, resultEntries, suggestedEntries],
  );
  const suggestedCount = suggestedEntries.length;
  const totalFound = foundView.data?.total ?? 0;

  // ---- the chosen candidate ------------------------------------------------
  const [chosen, setChosen] = useState('');
  const preselected = useRef(false);
  // The engine's one pick is ticked for the person only when a code or barcode
  // is equal AND the names agree: the same rule that ticks a pair in a batch.
  // It is still a choice they confirm, and the button names the article.
  useEffect(() => {
    if (preselected.current || !needsMatch || chosen || !canSave) return;
    const first = suggestions.data?.items[0];
    if (first && first.bulk === 'selected' && (first.article.held_by ?? []).length === 0) {
      preselected.current = true;
      setChosen(first.article.code);
    }
  }, [suggestions.data, needsMatch, chosen, canSave]);
  const chosenEntry = candidates.find((entry) => entry.article.code === chosen);
  useEffect(() => {
    // A candidate that is no longer listed is no longer chosen.
    if (chosen && candidates.length > 0 && !chosenEntry) setChosen('');
  }, [chosen, chosenEntry, candidates.length]);

  // ---- saving ---------------------------------------------------------------
  // Asks the store to give this product an article's code; '' clears it. A move
  // clears the one product that carries the code first, in the same request.
  const use = useCallback(
    async (articleCode: string, articleName: string, move?: { from: string }): Promise<ErpMatchOutcome | undefined> => {
      const key = idempotency.key();
      const outcome = await action.run('save', async () => {
        try {
          const result = await erpApi.setMatch(
            connection.id,
            { installation_id: target.store.id, website_code: target.website.code, sku: row.sku, article_code: articleCode, ...(move ? { move: true } : {}) },
            key,
          );
          idempotency.settle();
          return result;
        } catch (cause) {
          idempotency.settle(cause);
          throw cause;
        }
      });
      if (!outcome) return undefined;
      onChanged();
      if (outcome.status === 'unchanged' && !outcome.pending) action.setNotice('The store already has that code for this product.');
      const ids = [outcome.pending?.id, outcome.released?.id].filter((id): id is string => Boolean(id));
      if (ids.length > 0) {
        onSaved({
          sku: row.sku,
          productLabel,
          articleLabel: articleCode ? `${articleCode}${articleName ? ` ${articleName}` : ''}` : 'no article (match removed)',
          requestIds: ids,
        });
      }
      return outcome;
    },
    [action, idempotency, connection.id, target.store.id, target.website.code, row.sku, productLabel, onChanged, onSaved],
  );
  const saving = action.busy === 'save';

  const matchAndNext = useCallback(async () => {
    if (!canSave || !chosenEntry || saving) return;
    const outcome = await use(chosenEntry.article.code, chosenEntry.article.name);
    // Nothing was asked of the store when it already has the code: stay, and say so.
    if (outcome && (outcome.pending || outcome.status !== 'unchanged')) onAdvance();
  }, [canSave, chosenEntry, saving, use, onAdvance]);

  // ---- keyboard ---------------------------------------------------------------
  const latest = useRef({ matchAndNext, candidates, onSkip, onStep, onUndo, canUndo, canSave });
  latest.current = { matchAndNext, candidates, onSkip, onStep, onUndo, canUndo, canSave };
  useEffect(() => {
    if (!shortcutsOn) return undefined;
    function onKey(event: KeyboardEvent) {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      // A dialog is open: its own keys apply.
      if (document.querySelector('dialog[open]')) return;
      // Letters are shortcuts only while nothing is being typed.
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) && !(target instanceof HTMLInputElement && (target.type === 'radio' || target.type === 'checkbox'))) return;
      const state = latest.current;
      const key = event.key;
      if (/^[1-9]$/.test(key)) {
        const entry = state.candidates[Number(key) - 1];
        if (state.canSave && entry && (entry.article.held_by ?? []).length === 0) {
          event.preventDefault();
          setChosen(entry.article.code);
          document.querySelector<HTMLInputElement>(`[data-candidate-code="${CSS.escape(entry.article.code)}"]`)?.focus();
        }
        return;
      }
      switch (key.toLowerCase()) {
        case 'm':
          if (state.canSave) {
            event.preventDefault();
            void state.matchAndNext();
          }
          break;
        case 'enter':
          // Enter confirms only where it would do nothing else: not on a button or a link.
          if (state.canSave && (!target || target === document.body || (target instanceof HTMLInputElement && target.type === 'radio'))) {
            event.preventDefault();
            void state.matchAndNext();
          }
          break;
        case 's':
          event.preventDefault();
          state.onSkip();
          break;
        case 'u':
          if (state.canUndo) {
            event.preventDefault();
            state.onUndo();
          }
          break;
        case 'j':
        case ']':
          event.preventDefault();
          state.onStep(1);
          break;
        case 'k':
        case '[':
          event.preventDefault();
          state.onStep(-1);
          break;
        case '/':
          event.preventDefault();
          searchRef.current?.focus();
          searchRef.current?.select();
          break;
        default:
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shortcutsOn]);

  const cannot = canManage ? cannotSaveText(save, words) : null;
  const actionLabel = matched ? 'Replace match and next' : 'Match and next';
  const chosenCode = chosenEntry?.article.code;

  // One candidate card; its number is its place on screen, across both lists.
  const card = (entry: Candidate, index: number) => (
    <CandidateCard
      key={entry.article.code}
      article={entry.article}
      reasons={entry.reasons}
      nameCheck={entry.nameCheck}
      ambiguous={entry.ambiguous}
      shortcut={index + 1}
      currency={currency}
      pricesIncludeTax={foundView.data?.prices_include_tax ?? suggestions.data?.prices_include_tax ?? pricesIncludeTax}
      selected={chosen === entry.article.code}
      onSelect={canSave ? () => setChosen(entry.article.code) : undefined}
      groupName={groupName}
      copyable={copyable && !canSave}
      onMove={
        canSave
          ? (holder: ErpArticleHolder) =>
              setMoving({ code: entry.article.code, articleName: entry.article.name || entry.article.code, holder: { sku: holder.sku, name: holder.name || holder.sku }, keep: false })
          : undefined
      }
      moving={saving}
      headingLevel={5}
    />
  );
  const suggestionsBlock = needsMatch ? (
    <div className="match-candidates__group" data-testid="match-suggestions">
      <h4 className="match-section__title">
        Suggested
        <HelpTip label="What the suggestions are based on">
          <p>{matchEvidenceLegend}</p>
        </HelpTip>
      </h4>
      <ErrorNotice error={suggestions.error} />
      {suggestions.loading && !suggestions.data ? (
        <LoadingRows label="Looking for suggestions" rows={2} rowHeight="92px" />
      ) : suggestedCount === 0 ? (
        <p className="quiet-empty">No suggestions</p>
      ) : null}
      {suggestedCount > 0 ? (
        <div role="radiogroup" aria-label="Suggested ERP articles" className="match-candidates__list">
          {suggestedEntries.map((entry, index) => card(entry, (resultsFirst ? resultEntries.length : 0) + index))}
        </div>
      ) : null}
    </div>
  ) : null;
  const resultsBlock = asking ? (
    <div className="match-candidates__group" data-testid="match-results" aria-busy={foundView.stale || undefined}>
      {needsMatch ? <h4 className="match-section__title">Search results</h4> : null}
      <ErrorNotice error={found.error} />
      {found.loading && !foundView.data ? <LoadingRows label="Searching the ERP" rows={2} rowHeight="92px" /> : null}
      {found.data && found.data.items.length === 0 ? (
        <p className="quiet-empty" data-testid="match-finder-empty">
          No article matches “{query}”.{freeOnly ? ' Articles another product uses are hidden; untick the box to find one and move its match here.' : ''}
        </p>
      ) : null}
      {resultEntries.length > 0 ? (
        <div role="radiogroup" aria-label="ERP search results" className="match-candidates__list">
          {resultEntries.map((entry, index) => card(entry, (resultsFirst ? 0 : suggestedCount) + index))}
        </div>
      ) : null}
      {foundView.data && foundView.data.items.length > 0 ? (
        <p className="match-candidates__count" role="status">
          {totalFound === 1 ? '1 article' : `${formatNumber(totalFound)} articles`} for “{query}”
        </p>
      ) : null}
      {foundView.data && foundView.data.items.length < totalFound ? (
        <button type="button" className="button button--secondary" disabled={foundView.stale} onClick={() => setShown(shown + SEARCH_STEP)}>
          Show more articles
        </button>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="match-work" data-testid="match-desk">
      {/* ---- the store's product, and what to do with it ---- */}
      <div className="match-side">
      <section className="match-product" aria-labelledby="match-product-title" data-testid="match-product">
        <div className="match-product__nav">
          <button type="button" className="button button--secondary button--small match-desk__back" onClick={onBack}>
            Back to the queue
          </button>
          <p className="match-product__place" data-testid="match-place">
            Product {formatNumber(place.number)} of {formatNumber(place.total)}
            {skipped ? ' · skipped' : ''}
          </p>
          <div className="match-product__step">
            <button type="button" className="icon-button icon-button--ghost" aria-label="Previous product" aria-keyshortcuts="K" disabled={!place.hasPrev} onClick={() => onStep(-1)}>
              <Icon name="chevron-up" size={16} />
            </button>
            <button type="button" className="icon-button icon-button--ghost" aria-label="Next product" aria-keyshortcuts="J" disabled={!place.hasNext} onClick={() => onStep(1)}>
              <Icon name="chevron-down" size={16} />
            </button>
          </div>
        </div>
        <header className="match-product__head">
          <h3 id="match-product-title" ref={headingRef} tabIndex={-1} className="match-product__title">
            {productLabel}
          </h3>
          <div className="match-product__ids">
            <code className="match-product__sku">{row.sku}</code>
            <StatusBadge kind="matchState" value={row.state} />
          </div>
        </header>

        <dl className="match-facts match-facts--store" aria-label="In the store">
          <div>
            <dt>{bySku ? 'SKU' : attribute ? `Value of “${attribute}”` : 'ERP code'}</dt>
            <dd>{bySku ? <code>{row.sku}</code> : row.erp_code ? <code>{row.erp_code}</code> : <span className="muted">Empty</span>}</dd>
          </div>
          <div>
            <dt>Website</dt>
            <dd>{target.website.name || target.store.name}</dd>
          </div>
          <div className="match-facts__wide">
            <dt>Stock</dt>
            <dd>{row.stock_managed ? 'Managed in the store' : 'Not managed in the store, so it receives no quantity from the ERP'}</dd>
          </div>
        </dl>

        {row.pending ? <PendingNotice pending={row.pending} attribute={attribute} busy={saving} onRetry={canSave ? () => void use(row.pending!.requested_code, '') : undefined} /> : null}
        <ErrorNotice error={action.error} />
        {action.notice ? (
          <Callout tone="success" testId="match-saved">
            {action.notice}
          </Callout>
        ) : null}

        {matched && row.article ? (
          <div className="match-product__section" data-testid="match-matched">
            <h4 className="match-section__title">Matched ERP article</h4>
            <CandidateCard article={row.article} currency={currency} pricesIncludeTax={pricesIncludeTax} headingLevel={5} />
            {canSave && !row.pending ? (
              <div className="match-product__remove">
                <button type="button" className="button button--secondary button--small" disabled={saving} onClick={() => setRemoving(true)}>
                  Remove match
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {row.state === 'conflict' || (row.conflicts_with?.length ?? 0) > 0 ? (
          <div className="match-product__section" data-testid="match-conflict">
            <h4 className="match-section__title">{otherWebsites.length > 0 ? 'Why this product takes nothing from the ERP' : 'Same code as another product'}</h4>
            {otherWebsites.length > 0 ? (
              <p>
                It is also in the {otherWebsites.join(', ')} website, which takes stock from the ERP too. Which website’s stock and price it should have is
                not guessed, so it is sent nothing until it is in one of them.
              </p>
            ) : (
              <p>Another product has the same code. Each takes this article’s stock and price from the ERP, so its stock is counted for both.</p>
            )}
            {canSave && !row.pending && row.erp_code && row.conflicts_with?.length === 1 && otherWebsites.length === 0 ? (
              <button
                type="button"
                className="button button--small"
                disabled={saving}
                onClick={() => setMoving({ code: row.erp_code, articleName: row.erp_code, holder: { sku: row.conflicts_with![0]!, name: row.conflicts_with![0]! }, keep: true })}
              >
                Keep the match here
              </button>
            ) : null}
            {row.conflicts_with && row.conflicts_with.length > 0 ? (
              <ul className="match-conflicts" aria-label="Products with the same code">
                {row.conflicts_with.map((sku) => (
                  <li key={sku}>
                    <code>{sku}</code>
                    <button type="button" className="button button--secondary button--small" onClick={() => onShowProduct(sku)}>
                      Show {sku}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

      </section>
        <div className="match-product__actions">
          {cannot ? null : (
            <button
              type="button"
              className="button button--primary match-product__go"
              disabled={!canSave || !chosenEntry || saving}
              aria-keyshortcuts="M"
              onClick={() => void matchAndNext()}
              data-testid="match-and-next"
            >
              {saving ? 'Asking the store' : chosenCode ? `${actionLabel}: ${chosenCode}` : actionLabel}
              <kbd className="kbd kbd--on-primary" aria-hidden="true">
                M
              </kbd>
            </button>
          )}
          <button type="button" className="button button--secondary" aria-keyshortcuts="S" onClick={onSkip} data-testid="match-skip">
            {cannot ? 'Next product' : 'Skip'}
            <kbd className="kbd" aria-hidden="true">
              S
            </kbd>
          </button>
        </div>
        {status}
        {!canManage ? (
          <p className="field__help">Saving a match needs permission to manage ERP integrations.</p>
        ) : cannot ? (
          <p className="field__help" data-testid="match-cannot-hint">
            Copy a code and enter it in {words.admin}.
          </p>
        ) : null}
        <Shortcuts />
      </div>

      {/* ---- the ERP's candidates, with the search beside the product ---- */}
      <section className="match-candidates" aria-label="ERP candidates" data-testid="match-candidates">
        <div className="match-candidates__search field">
          <label htmlFor={searchId}>
            {matched ? 'Search for a different article' : 'Search the ERP'}
          </label>
          <Input
            id={searchId}
            ref={searchRef}
            name="article-search"
            type="search"
            value={text}
            placeholder="Name, code or barcode (every word must match)…"
            autoComplete="off"
            spellCheck={false}
            aria-keyshortcuts="/"
            onChange={(event) => {
              setTouched(true);
              setText(event.target.value);
            }}
            onFocus={(event) => event.currentTarget.select()}
          />
          <Checkbox
            id={`${searchId}-free`}
            className="match-candidates__free"
            checked={freeOnly}
            onChange={(event) => setFreeOnly(event.target.checked)}
            label="Only articles no product uses yet"
          />
        </div>

        <div className="match-candidates__lists" data-testid="match-candidate-list">
          {resultsFirst ? (
            <>
              {resultsBlock}
              {suggestionsBlock}
            </>
          ) : (
            <>
              {suggestionsBlock}
              {resultsBlock}
            </>
          )}
        </div>

      </section>

      <ConfirmDialog
        open={moving !== null}
        title={moving?.keep ? 'Keep this match here?' : 'Move this match here?'}
        description={
          moving
            ? moving.keep
              ? `${moving.holder.name} will lose the code ${moving.code} and stop taking stock, prices and product data from the ERP; it keeps the stock and price it has now. ${productLabel} keeps the code and starts taking them from the ERP. Both changes are asked of the store together.`
              : `${moving.holder.name} (${moving.holder.sku}) will stop taking stock, prices and product data from ${moving.articleName}; it keeps the stock and price it has now. ${productLabel} will take them from it instead. Both changes are asked of the store together; if the store cannot save the new match, ${moving.holder.name} stays without one.`
            : ''
        }
        confirmLabel={moving?.keep ? 'Keep the match here' : 'Move the match'}
        busy={saving}
        onConfirm={() => {
          const code = moving?.code ?? '';
          const name = moving?.articleName ?? '';
          const from = moving ? `${moving.holder.name} (${moving.holder.sku})` : '';
          setMoving(null);
          void use(code, name, { from }).then((outcome) => {
            if (outcome?.released) action.setNotice(`The store was asked to take the code from ${from} and give it to this product. Both show as saving until the store answers.`);
          });
        }}
        onCancel={() => setMoving(null)}
      />
      <ConfirmDialog
        open={removing}
        title="Remove this match?"
        description={`${productLabel} will stop taking stock, prices and product data from ${row.article?.name ?? 'the ERP'}. The store keeps the stock and price it has now. You can match it again whenever you like, or undo this.`}
        confirmLabel="Remove match"
        busy={saving}
        onConfirm={() => {
          setRemoving(false);
          void use('', '');
        }}
        onCancel={() => setRemoving(false)}
      />
    </div>
  );
}

/** The shortcuts, written where they are used: a keyboard person should not have to guess. */
function Shortcuts() {
  return (
    <details className="match-shortcuts" data-testid="match-shortcuts">
      <summary>Keyboard shortcuts</summary>
      <dl>
        <div>
          <dt>
            <kbd className="kbd">1</kbd>–<kbd className="kbd">9</kbd>
          </dt>
          <dd>Choose a candidate</dd>
        </div>
        <div>
          <dt>
            <kbd className="kbd">M</kbd> or <kbd className="kbd">Enter</kbd>
          </dt>
          <dd>Match and go to the next product</dd>
        </div>
        <div>
          <dt>
            <kbd className="kbd">S</kbd>
          </dt>
          <dd>Skip this product</dd>
        </div>
        <div>
          <dt>
            <kbd className="kbd">U</kbd>
          </dt>
          <dd>Undo the last match</dd>
        </div>
        <div>
          <dt>
            <kbd className="kbd">J</kbd> <kbd className="kbd">K</kbd>
          </dt>
          <dd>Next or previous product</dd>
        </div>
        <div>
          <dt>
            <kbd className="kbd">/</kbd>
          </dt>
          <dd>Search the ERP</dd>
        </div>
      </dl>
    </details>
  );
}

/** What became of a request to save a match: waiting, refused, or not answered. */
function PendingNotice({ pending, attribute, busy, onRetry }: { pending: ErpPendingMatch; attribute: string; busy: boolean; onRetry?: () => void }) {
  const clearing = pending.requested_code === '';
  if (pending.state === 'pending') {
    return (
      <Callout tone="info" testId="match-pending">
        {clearing
          ? `Removing the “${attribute}” value. Waiting for the store.`
          : `Saving ${pending.requested_code} as the “${attribute}” value. Waiting for the store.`}
      </Callout>
    );
  }
  const retry = onRetry ? (
    <button type="button" className="button button--secondary button--small" disabled={busy} onClick={onRetry}>
      Try again
    </button>
  ) : undefined;
  return (
    <Callout
      tone={pending.state === 'rejected' ? 'danger' : 'warning'}
      title={pending.state === 'rejected' ? 'The store did not save it' : 'The store has not confirmed it'}
      testId="match-failed"
      actions={retry}
    >
      {refusalText(pending.state === 'expired' ? 'no_answer' : pending.reason)}
    </Callout>
  );
}
